import {
  assertSafeInteger,
  OPEN_WORLD_CONTENT_KINDS,
  type OpenWorldContentKind,
} from '@kenocyber/shared';
import { PVP_TICK_MS } from '../pvp/balance';
import { pvpTickTravelMu } from '../pvp/movement';

/**
 * How big a quadrant is, and the space rules inside it.
 *
 * ## The size is derived, never chosen
 *
 * The requirement is that a base ship with no bonuses takes about four minutes
 * to cross one side in a straight line, using the movement that already
 * decides where a hull ends up in a duel. So the side is computed from that
 * movement rather than written down beside it: {@link pvpTickTravelMu} is the
 * one function that says how far a tick travels, {@link PVP_TICK_MS} is how
 * long a tick lasts, and everything here follows from the two.
 *
 * Anything that changes the ship's base speed therefore changes the size of the
 * world, which is the correct coupling: the requirement was expressed in
 * minutes, and minutes are speed and distance. Copying `17_000` into this file
 * would have made the four minutes a coincidence that nobody would notice
 * breaking.
 *
 * ## Why position is quadrant plus offset, and never one global number
 *
 * A single world coordinate would run to ten times a quadrant's side, and every
 * distance test squares it. Concretely, with the constants below: a local axis
 * reaches about 8.2e7 milli-units, and `dx*dx + dy*dy` across a whole quadrant
 * already exceeds `Number.MAX_SAFE_INTEGER` - which is why
 * {@link isAtLeastApartMu} exists and refuses to square anything it does not
 * have to. Globally the same sum would reach about 1.3e18, roughly a hundred
 * and fifty times past the exact-integer range, so every comparison in the game
 * would have to move to `bigint` or to floats. Floats are how a client's
 * prediction and a server's authority end up one unit apart forever, and this
 * project does not have a float in a simulation anywhere.
 *
 * A world of 26x26 makes that worse by another factor of seven. Local
 * coordinates make it not a question: a position is `(coordinate, x, y)`, the
 * offset never leaves its quadrant, and the arithmetic is the same at any world
 * size.
 *
 * ## Margins and gaps are expressed in SECONDS
 *
 * A margin of `1_700_000` is a number nobody can sanity-check; a margin of five
 * seconds of flight is a number anyone can. Both derive from the same travel
 * speed, so they stay proportionate if the ship's base speed ever moves.
 */

/** Ticks in a second. Whole, and asserted - a fractional tick rate is a bug. */
const TICKS_PER_SECOND = 1000 / PVP_TICK_MS;
if (!Number.isInteger(TICKS_PER_SECOND) || TICKS_PER_SECOND <= 0) {
  throw new RangeError(`PVP_TICK_MS (${PVP_TICK_MS}) must divide one second exactly`);
}

/**
 * How far an unmodified hull travels in one second, in milli-units.
 *
 * `pvpTickTravelMu()` with no argument is the base speed - 10 000 basis points,
 * no rune, no jewel, no booster. Read, never copied.
 */
export const OPEN_WORLD_BASE_TRAVEL_MU_PER_SECOND = pvpTickTravelMu() * TICKS_PER_SECOND;

/** The requirement, stated once: roughly four minutes from one side to the other. */
export const OPEN_WORLD_QUADRANT_CROSSING_SECONDS = 240;

/**
 * One quadrant's side, in milli-units. Square quadrants.
 *
 * With today's constants: 17 000 mu per tick, 20 ticks per second, 240 seconds
 * - 81 600 000 milli-units, or 81 600 arena units a side.
 */
export const OPEN_WORLD_QUADRANT_SIDE_MU =
  OPEN_WORLD_BASE_TRAVEL_MU_PER_SECOND * OPEN_WORLD_QUADRANT_CROSSING_SECONDS;

/** Nothing is placed within this of an edge, in seconds of flight. */
export const OPEN_WORLD_BORDER_MARGIN_SECONDS = 5;

/** The same, in milli-units. */
export const OPEN_WORLD_BORDER_MARGIN_MU =
  OPEN_WORLD_BASE_TRAVEL_MU_PER_SECOND * OPEN_WORLD_BORDER_MARGIN_SECONDS;

/**
 * The box content may actually occupy, in milli-units, inclusive.
 *
 * A reserved border rather than the whole quadrant, because a crossing happens
 * at an edge: a boss sitting on the boundary would be fought half in one
 * quadrant and half in the next, and a comet in the corner would be in four.
 */
export const OPEN_WORLD_CONTENT_MIN_MU = OPEN_WORLD_BORDER_MARGIN_MU;
export const OPEN_WORLD_CONTENT_MAX_MU = OPEN_WORLD_QUADRANT_SIDE_MU - OPEN_WORLD_BORDER_MARGIN_MU;

/** A position inside one quadrant. Integer milli-units, both axes. */
export interface OpenWorldLocalPosition {
  readonly x: number;
  readonly y: number;
}

/**
 * How far apart two spaces of a given kind must be, in seconds of flight.
 *
 * Two spaces need `max(gap(a), gap(b))` between them, so a mob never lands
 * inside a boss's clearance just because mobs are allowed to crowd each other.
 * Large structures - water, planets, bosses - keep ten seconds; comets and mobs
 * are smaller and keep less.
 *
 * These are spacing rules, not balance: they say what may occupy the same
 * space, and nothing about what any of it is worth.
 */
export const OPEN_WORLD_SPACE_GAP_SECONDS: Readonly<Record<OpenWorldContentKind, number>> =
  Object.freeze({
    water_area: 10,
    mining_planet: 10,
    boss: 10,
    comet: 3,
    mob: 2,
  });

/**
 * The same table in milli-units, derived once.
 *
 * Typed against {@link OpenWorldContentKind} rather than inferred from its own
 * keys, so a sixth content kind is a compile error here instead of a silently
 * missing clearance that would let the new thing land inside a boss.
 */
export const OPEN_WORLD_SPACE_GAP_MU: Readonly<Record<OpenWorldContentKind, number>> =
  Object.freeze(
    Object.fromEntries(
      OPEN_WORLD_CONTENT_KINDS.map((kind) => [
        kind,
        OPEN_WORLD_BASE_TRAVEL_MU_PER_SECOND * OPEN_WORLD_SPACE_GAP_SECONDS[kind],
      ]),
    ) as Record<OpenWorldContentKind, number>,
  );

/** The widest clearance any kind asks for. The packing check uses it. */
export const OPEN_WORLD_MAX_SPACE_GAP_MU = Math.max(
  ...OPEN_WORLD_CONTENT_KINDS.map((kind) => OPEN_WORLD_SPACE_GAP_MU[kind]),
);

/**
 * How far apart two spaces must be: whichever of the two asks for more.
 *
 * So a mob never lands inside a boss's clearance merely because mobs are
 * allowed to crowd each other.
 */
export function openWorldRequiredGapMu(
  first: OpenWorldContentKind,
  second: OpenWorldContentKind,
): number {
  return Math.max(OPEN_WORLD_SPACE_GAP_MU[first], OPEN_WORLD_SPACE_GAP_MU[second]);
}

/**
 * Are these two points at least `minimum` apart, exactly and without overflow?
 *
 * The early return is not an optimisation, it is the correctness of the whole
 * function. A quadrant is wide enough that `dx*dx + dy*dy` across it leaves the
 * exact-integer range, so squaring first would give a wrong answer for far
 * apart pairs - the ones that are obviously fine. When either axis alone
 * already covers the distance the answer is yes without arithmetic; only when
 * both axes are SHORTER than the minimum is anything squared, and then both
 * operands are below the minimum by construction.
 *
 * {@link assertOpenWorldGeometryIsPossible} proves the surviving multiplication
 * stays exact.
 */
export function isAtLeastApartMu(
  first: OpenWorldLocalPosition,
  second: OpenWorldLocalPosition,
  minimumMu: number,
): boolean {
  const dx = Math.abs(first.x - second.x);
  const dy = Math.abs(first.y - second.y);
  if (dx >= minimumMu || dy >= minimumMu) {
    return true;
  }
  return dx * dx + dy * dy >= minimumMu * minimumMu;
}

/**
 * How many spaces the largest possible quadrant has to hold.
 *
 * The sum of every kind's maximum. Passed in rather than imported so the check
 * cannot silently disagree with the counts the generator actually uses.
 */
export interface OpenWorldPackingDemand {
  readonly totalSpaces: number;
}

/**
 * Fails loudly if the configuration cannot be satisfied.
 *
 * Called at module load by the generator. The alternative - discovering it at
 * generation time - is a rejection loop that spins until an attempt cap trips,
 * on one unlucky quadrant, in production, months later. A configuration that
 * cannot work should not start.
 *
 * The packing test is deliberately conservative: it asks whether every space
 * would fit on a square lattice spaced at the WIDEST clearance any kind asks
 * for. Real placements mix kinds and need less, so passing this is sufficient
 * and never merely likely.
 */
export function assertOpenWorldGeometryIsPossible(demand: OpenWorldPackingDemand): void {
  assertSafeInteger(OPEN_WORLD_QUADRANT_SIDE_MU, 'open world quadrant side');
  assertSafeInteger(demand.totalSpaces, 'open world total spaces');

  const usable = OPEN_WORLD_CONTENT_MAX_MU - OPEN_WORLD_CONTENT_MIN_MU;
  if (usable <= 0) {
    throw new RangeError(
      `open world border margin (${OPEN_WORLD_BORDER_MARGIN_MU} mu) leaves no room inside a ${OPEN_WORLD_QUADRANT_SIDE_MU} mu quadrant`,
    );
  }

  // The exactness proof for `isAtLeastApartMu`: both squared operands are
  // strictly below the widest gap, so this sum bounds every multiplication the
  // function can ever perform.
  const widestSquaredSum = 2 * OPEN_WORLD_MAX_SPACE_GAP_MU * OPEN_WORLD_MAX_SPACE_GAP_MU;
  if (!Number.isSafeInteger(widestSquaredSum)) {
    throw new RangeError(
      `open world clearance (${OPEN_WORLD_MAX_SPACE_GAP_MU} mu) is too wide to compare exactly in integers`,
    );
  }

  const perAxis = Math.floor(usable / OPEN_WORLD_MAX_SPACE_GAP_MU) + 1;
  const capacity = perAxis * perAxis;
  if (capacity < demand.totalSpaces) {
    throw new RangeError(
      `open world quadrant fits ${capacity} spaced positions but the catalogue may need ${demand.totalSpaces}`,
    );
  }
}
